import React, { useState, useEffect, useCallback } from 'react';
import './UserManager.css';
import { apiFetch } from '../api/api';
import Toast from '../components/Toast';

export default function UserManager() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create', 'edit', 'password'
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role: 'read-only',
    email: '',
    full_name: '',
    status: 'active'
  });
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const showToast = useCallback((message, type = 'success') => {
    setToast({ show: true, message, type });
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiFetch('/api/auth/users');
      setUsers(data);
    } catch (error) {
      console.error('Error loading users:', error);
      showToast('Failed to load users', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleCreateUser = () => {
    setModalMode('create');
    setSelectedUser(null);
    setFormData({
      username: '',
      password: '',
      role: 'read-only',
      email: '',
      full_name: '',
      status: 'active'
    });
    setShowModal(true);
  };

  const handleEditUser = (user) => {
    setModalMode('edit');
    setSelectedUser(user);
    setFormData({
      username: user.username,
      password: '',
      role: user.role,
      email: user.email || '',
      full_name: user.full_name || '',
      status: user.status
    });
    setShowModal(true);
  };

  const handleResetPassword = (user) => {
    setModalMode('password');
    setSelectedUser(user);
    setFormData({
      username: user.username,
      password: '',
      role: user.role,
      email: user.email || '',
      full_name: user.full_name || '',
      status: user.status
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (modalMode === 'create') {
        await apiFetch('/api/auth/users', {
          method: 'POST',
          body: JSON.stringify({
            username: formData.username,
            password: formData.password,
            role: formData.role,
            email: formData.email,
            full_name: formData.full_name
          })
        });
        showToast('User created successfully');
      } else if (modalMode === 'edit') {
        await apiFetch(`/api/auth/users/${selectedUser.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            username: formData.username,
            role: formData.role,
            email: formData.email,
            full_name: formData.full_name,
            status: formData.status
          })
        });
        showToast('User updated successfully');
      } else if (modalMode === 'password') {
        await apiFetch(`/api/auth/users/${selectedUser.id}/password`, {
          method: 'PUT',
          body: JSON.stringify({
            password: formData.password
          })
        });
        showToast('Password reset successfully');
      }

      setShowModal(false);
      loadUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      showToast(error.message || 'Failed to save user', 'error');
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Are you sure you want to delete user "${user.username}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await apiFetch(`/api/auth/users/${user.id}`, {
        method: 'DELETE'
      });
      showToast('User deleted successfully');
      loadUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      showToast(error.message || 'Failed to delete user', 'error');
    }
  };

  const handleStatusChange = async (user, newStatus) => {
    try {
      await apiFetch(`/api/auth/users/${user.id}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus })
      });
      showToast(`User ${newStatus === 'active' ? 'activated' : newStatus} successfully`);
      loadUsers();
    } catch (error) {
      console.error('Error updating user status:', error);
      showToast(error.message || 'Failed to update user status', 'error');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (user.full_name && user.full_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    const matchesStatus = filterStatus === 'all' || user.status === filterStatus;
    
    return matchesSearch && matchesRole && matchesStatus;
  });

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'role-admin';
      case 'read-write': return 'role-readwrite';
      case 'read-only': return 'role-readonly';
      default: return '';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'status-active';
      case 'suspended': return 'status-suspended';
      case 'inactive': return 'status-inactive';
      default: return '';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString() + ' ' + new Date(dateString).toLocaleTimeString();
  };

  if (loading) {
    return (
      <div className="user-manager">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-manager">
      <div className="user-manager-header">
        <h1>User Management</h1>
        <button className="btn btn-primary" onClick={handleCreateUser}>
          <span className="btn-icon">👤</span>
          Create User
        </button>
      </div>

      <div className="user-filters">
        <div className="filter-group">
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="filter-group">
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Roles</option>
            <option value="admin">Admin</option>
            <option value="read-write">Read-Write</option>
            <option value="read-only">Read-Only</option>
          </select>
        </div>

        <div className="filter-group">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        <div className="user-count">
          {filteredUsers.length} of {users.length} users
        </div>
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Username</th>
              <th>Full Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Last Login</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(user => (
              <tr key={user.id} className={`user-row ${user.status}`}>
                <td className="username-cell">
                  <strong>{user.username}</strong>
                </td>
                <td>{user.full_name || '-'}</td>
                <td>{user.email || '-'}</td>
                <td>
                  <span className={`role-badge ${getRoleColor(user.role)}`}>
                    {user.role}
                  </span>
                </td>
                <td>
                  <span className={`status-badge ${getStatusColor(user.status)}`}>
                    {user.status}
                  </span>
                </td>
                <td className="date-cell">
                  {formatDate(user.last_login)}
                </td>
                <td className="date-cell">
                  {formatDate(user.created_at)}
                </td>
                <td className="actions-cell">
                  <div className="action-buttons">
                    <button 
                      className="btn btn-small btn-secondary"
                      onClick={() => handleEditUser(user)}
                      title="Edit User"
                    >
                      ✏️
                    </button>
                    <button 
                      className="btn btn-small btn-warning"
                      onClick={() => handleResetPassword(user)}
                      title="Reset Password"
                    >
                      🔑
                    </button>
                    {user.status === 'active' ? (
                      <button 
                        className="btn btn-small btn-danger"
                        onClick={() => handleStatusChange(user, 'suspended')}
                        title="Suspend User"
                      >
                        ⏸️
                      </button>
                    ) : (
                      <button 
                        className="btn btn-small btn-success"
                        onClick={() => handleStatusChange(user, 'active')}
                        title="Activate User"
                      >
                        ✅
                      </button>
                    )}
                    <button 
                      className="btn btn-small btn-danger"
                      onClick={() => handleDeleteUser(user)}
                      title="Delete User"
                    >
                      🗑️
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredUsers.length === 0 && (
          <div className="no-users">
            <div className="no-users-icon">👤</div>
            <h3>No users found</h3>
            <p>
              {searchTerm || filterRole !== 'all' || filterStatus !== 'all' 
                ? 'No users match your current filters.' 
                : 'No users have been created yet.'
              }
            </p>
          </div>
        )}
      </div>

      {/* User Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>
                {modalMode === 'create' && 'Create New User'}
                {modalMode === 'edit' && 'Edit User'}
                {modalMode === 'password' && 'Reset Password'}
              </h3>
              <button className="btn-close" onClick={() => setShowModal(false)}>×</button>
            </div>

            <form onSubmit={handleSubmit} className="modal-content">
              {modalMode !== 'password' && (
                <>
                  <div className="form-group">
                    <label htmlFor="username">Username *</label>
                    <input
                      type="text"
                      id="username"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      required
                      disabled={modalMode === 'edit'}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="full_name">Full Name</label>
                    <input
                      type="text"
                      id="full_name"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="role">Role *</label>
                    <select
                      id="role"
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="read-only">Read-Only</option>
                      <option value="read-write">Read-Write</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>

                  {modalMode === 'edit' && (
                    <div className="form-group">
                      <label htmlFor="status">Status *</label>
                      <select
                        id="status"
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="active">Active</option>
                        <option value="suspended">Suspended</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                  )}
                </>
              )}

              {(modalMode === 'create' || modalMode === 'password') && (
                <div className="form-group">
                  <label htmlFor="password">
                    {modalMode === 'create' ? 'Password *' : 'New Password *'}
                  </label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    minLength="6"
                    placeholder="Minimum 6 characters"
                  />
                </div>
              )}

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {modalMode === 'create' && 'Create User'}
                  {modalMode === 'edit' && 'Update User'}
                  {modalMode === 'password' && 'Reset Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ show: false, message: '', type: 'success' })}
        />
      )}
    </div>
  );
}
